import { DatePicker, Form, Input, Modal, Select, Switch } from "antd";
import TextArea from "antd/es/input/TextArea";
import { AppButton } from "components/button/AppButton";
import { WORK_MODELS } from "constants/general";
import { useGetSectors } from "features/jobs/hooks/useGetSectors";
import { useCreateWorkHistory } from "features/settings/hooks/useCreateWorkHistory";
import { QUERY_KEY_FOR_TALENT_PROFILE } from "features/settings/hooks/useGetTalentProfile";
import { ICreateWorkHistory } from "features/settings/types";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { IdentifierProps } from "types";
import { openNotification } from "utils/notification";
import dayjs from "dayjs";
import {
  QUERY_KEY_FOR_SINGLE_WORK_HISTORY,
  useGetSingleWork,
} from "features/settings/hooks/useGetSingleWork";
import { END_POINT } from "config/environment";

const requiredRule = (fieldName: string) => ({
  required: true,
  message: `${fieldName} is required`,
});

export const AddWorkHistory = ({ open, handleClose, id }: IdentifierProps) => {
  const [currentWork, setCurrentWork] = useState(false);
  const [form] = Form.useForm();
  const { token, userInfo } = useGetUserInfo();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useCreateWorkHistory();
  const { data: sectorData, isLoading: loadingSector } = useGetSectors();

  const { data } = useGetSingleWork({
    id: id as number,
    token,
  });
  const [jobTitles, setJobTitles] = useState<
    { label: string; value: string }[]
  >([]);
  const [isFormValid, setIsFormValid] = useState(false);

  const fetchJobTitles = async () => {
    try {
      const response = await fetch(`${END_POINT.BASE_URL}/v1/jobtitles`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        const titles = data.map((jobTitle: { name: string }) => ({
          label: jobTitle.name,
          value: jobTitle.name,
        }));
        setJobTitles(titles);
      } else {
        console.error("Job titles fetch failed:", data);
      }
    } catch (error) {
      console.error("Error fetching job titles:", error);
    }
  };

  useEffect(() => {
    fetchJobTitles();
  }, []);

  const parseDate = (dateString: string | null) => {
    if (!dateString) return null;
    if (dateString.match(/^\d{4}-\d{2}$/)) {
      return dayjs(dateString, "YYYY-MM");
    }
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dayjs(dateString, "YYYY-MM-DD");
    }
    if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return dayjs(dateString, "DD/MM/YYYY");
    }
    return dayjs(dateString);
  };

  useEffect(() => {
    if (!data || !id) return;

    const isCurrent = data.is_current === 1;
    setCurrentWork(isCurrent);

    let dateRange = undefined;
    let startDateOnly = undefined;

    if (data.start_date && data.end_date && !isCurrent) {
      dateRange = [parseDate(data.start_date), parseDate(data.end_date)];
    } else if (data.start_date && isCurrent) {
      startDateOnly = parseDate(data.start_date);
    } else if (data.start_date && !data.end_date) {
      startDateOnly = parseDate(data.start_date);
    }

    const descriptionArray = data?.description
      ? data.description.split("\n").filter(Boolean)
      : [""];

    const achievementsArray = (data as any)?.achievements
      ? (data as any).achievements.split("\n").filter(Boolean)
      : [];

    const formValues = {
      organization: data?.company,
      is_current: isCurrent,
      description: descriptionArray,
      achievements: achievementsArray,
      sector_id: data?.sector_id ? Number(data.sector_id) : undefined,
      work_type: data?.type,
      title: data?.title,
      date: dateRange,
      ss: startDateOnly,
    };

    form.setFieldsValue(formValues);
    // Trigger validation after setting values
    setTimeout(() => {
      form.validateFields().catch(() => {});
    }, 100);
  }, [form, data, id]);

  useEffect(() => {
    if (!open) {
      form.resetFields();

      form.setFieldsValue({
        is_current: false,
        description: [""],
        achievements: [""],
      });

      setCurrentWork(false);
      setIsFormValid(false);
    }
  }, [open, form]);

  const disableFutureDates = (current: dayjs.Dayjs) => {
    return current && current.isAfter(dayjs().endOf("day"));
  };

  const validateForm = () => {
    const values = form.getFieldsValue();

    const hasOrganization =
      values.organization && values.organization.trim().length > 0;
    const hasTitle = values.title && values.title.trim().length > 0;
    const hasWorkType = values.work_type && values.work_type.trim().length > 0;
    const hasSector = values.sector_id;

    const hasValidDescription =
      values.description &&
      values.description.some((item: string) => item && item.trim().length > 0);

    let hasValidDate = false;
    if (currentWork) {
      hasValidDate = values.ss !== null && values.ss !== undefined;
    } else {
      hasValidDate =
        values.date && values.date.length === 2 && values.date[0] !== null;
    }

    const isValid =
      hasOrganization &&
      hasTitle &&
      hasWorkType &&
      hasSector &&
      hasValidDate &&
      hasValidDescription;
    setIsFormValid(isValid);
  };

  const handleFieldsChange = () => {
    validateForm();
  };

  const handleSubmit = (formData: any) => {
    let startDate = null;
    let endDate = null;

    if (currentWork) {
      startDate = formData.ss ? formData.ss.format("YYYY-MM-DD") : null;
    } else {
      if (formData.date && formData.date.length === 2) {
        startDate = formData.date[0]
          ? formData.date[0].format("YYYY-MM-DD")
          : null;
        endDate = formData.date[1]
          ? formData.date[1].format("YYYY-MM-DD")
          : null;
      }
    }

    const descriptionString =
      formData.description
        ?.filter((item: string) => item && item.trim().length > 0)
        .join("\n") || "";

    const achievementsString =
      formData.achievements
        ?.filter((item: string) => item && item.trim().length > 0)
        .join("\n") || "";

    const props: ICreateWorkHistory = {
      id: id as number,
      talent_id: userInfo?.talent?.id,
      title: formData.title,
      sector_id: formData.sector_id,
      company: formData.organization,
      work_type: formData.work_type,
      start_date: startDate,
      end_date: endDate,
      is_current: currentWork,
      description: descriptionString,
      achievements: achievementsString,
      token,
    };

    mutate(props, {
      onError: (err: any) => {
        let errorTitle = "Error";
        let errorDescription = "An error occurred, please try again.";

        if (err.response && err.response.data && err.response.data.error) {
          errorTitle = "Error: " + err.response.data.error;
          errorDescription =
            err.response.data.description || "Please try again.";
        }

        openNotification({
          title: errorTitle,
          state: "error",
          description: errorDescription,
          duration: 6,
        });
      },
      onSuccess: (res: any) => {
        openNotification({
          title: "Success",
          state: "success",
          description: id
            ? "Work Experience Updated Successfully"
            : "Work Experience Created Successfully",
          duration: 4.5,
        });
        form.resetFields();
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_TALENT_PROFILE],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_SINGLE_WORK_HISTORY, id],
        });
        handleClose();
      },
    });
  };

  return (
    <Modal
      title={id ? "Edit Work Experience" : "Add Work Experience"}
      open={open}
      onCancel={() => handleClose(false)}
      footer={null}
      width="45rem"
      style={{ top: 10 }}
    >
      <div>
        <Form
          layout="vertical"
          requiredMark={true}
          onFinish={handleSubmit}
          form={form}
          onValuesChange={handleFieldsChange}
          initialValues={{
            is_current: false,
            description: [""],
            achievements: [""],
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Form.Item noStyle name="is_current">
              <Switch
                size="small"
                onChange={(val) => {
                  setCurrentWork(val);
                  setTimeout(() => validateForm(), 100);
                }}
              />
            </Form.Item>
            <span>I currently work here</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5">
            <Form.Item
              name="organization"
              label="Organization"
              rules={[requiredRule("Organization")]}
              validateTrigger="onBlur"
            >
              <Input placeholder="Enter Organization" />
            </Form.Item>

            <Form.Item
              name="title"
              label="Job Title"
              rules={[requiredRule("Job Title")]}
              validateTrigger="onBlur"
            >
              <Select
                options={jobTitles}
                showSearch
                placeholder="Select Job title"
                allowClear
                loading={jobTitles.length === 0}
              />
            </Form.Item>

            <Form.Item
              name="work_type"
              label="Work type"
              rules={[requiredRule("Work type")]}
              validateTrigger="onBlur"
            >
              <Select
                className="SelectTag w-full"
                options={WORK_MODELS}
                placeholder="Select"
              />
            </Form.Item>

            <Form.Item
              name="sector_id"
              label="Sector"
              rules={[requiredRule("Sector")]}
              validateTrigger="onBlur"
            >
              {loadingSector ? (
                <Select placeholder="Loading sectors..." loading={true} />
              ) : (
                <Select
                  className="SelectTag w-full"
                  showSearch
                  options={sectorData?.map((sector) => ({
                    value: sector.id,
                    label: sector.name,
                  }))}
                  placeholder="Select Sector"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              )}
            </Form.Item>

            {!currentWork ? (
              <Form.Item
                name="date"
                label="Start & End Date"
                rules={[requiredRule("Start and End Date")]}
                validateTrigger="onBlur"
              >
                <DatePicker.RangePicker
                  className="w-full sm:w-auto"
                  format="DD/MM/YYYY"
                  placeholder={["Start date", "End date"]}
                  disabledDate={disableFutureDates}
                />
              </Form.Item>
            ) : (
              <Form.Item
                name="ss"
                label="Start Date"
                rules={[requiredRule("Start Date")]}
                validateTrigger="onBlur"
              >
                <DatePicker
                  className="w-full sm:w-auto"
                  format="DD/MM/YYYY"
                  disabledDate={disableFutureDates}
                />
              </Form.Item>
            )}

            {/* Description / Responsibilities */}
            <div className="col-span-2 mb-8">
              <label className="font-medium text-[18px] block mb-2">
                Description <span className="text-red-500">*</span>
              </label>

              <div className="text-[#667085] mb-4">
                List your key responsibilities in this role
              </div>

              <Form.List name="description">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <div
                        key={field.key}
                        className="flex items-center gap-3 mb-3"
                      >
                        <div className="text-[28px] leading-none text-[#98A2B3]">
                          •
                        </div>

                        <Form.Item
                          {...field}
                          className="flex-1 mb-0"
                          rules={[
                            {
                              required: true,
                              message: "Please enter a responsibility",
                            },
                          ]}
                          validateTrigger="onBlur"
                        >
                          <Input
                            placeholder="e.g. Led product design for mobile app"
                            size="large"
                          />
                        </Form.Item>

                        {fields.length > 1 && (
                          <span
                            className="text-red-500 cursor-pointer text-xl"
                            onClick={() => remove(field.name)}
                          >
                            ⊗
                          </span>
                        )}
                      </div>
                    ))}

                    <div
                      className="cursor-pointer text-[#000080] font-semibold text-[18px]"
                      onClick={() => add("")}
                    >
                      + Add item
                    </div>
                  </>
                )}
              </Form.List>
            </div>

            {/* Achievements - Optional */}
            <div className="col-span-2">
              <label className="font-medium text-[18px] block mb-2">
                Achievements
              </label>

              <div className="text-[#667085] mb-4">
                Highlight your key accomplishments in this role (optional)
              </div>

              <Form.List name="achievements">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <div
                        key={field.key}
                        className="flex items-center gap-3 mb-3"
                      >
                        <div className="text-[18px] text-[#000080]">★</div>

                        <Form.Item {...field} className="flex-1 mb-0">
                          <Input
                            placeholder="e.g. Increased user retention by 30%"
                            size="large"
                          />
                        </Form.Item>

                        {fields.length > 1 && (
                          <span
                            className="text-red-500 cursor-pointer text-xl"
                            onClick={() => remove(field.name)}
                          >
                            ⊗
                          </span>
                        )}
                      </div>
                    ))}

                    <div
                      className="cursor-pointer text-[#000080] font-semibold text-[18px]"
                      onClick={() => add("")}
                    >
                      + Add item
                    </div>
                  </>
                )}
              </Form.List>
            </div>
          </div>
          <div className="flex mt-4">
            <AppButton
              type="submit"
              isLoading={isLoading}
              disabled={!isFormValid}
            />
          </div>
        </Form>
      </div>
    </Modal>
  );
};
